import React, { FC, useEffect, useState } from 'react';
import { CancelSVG } from '../../components/svgs';
import { FilterOption } from '../../components/custom-input/FilterDropdown';
import MultiSelectDropdown from '../../components/custom-input/multiSelectDropdown';
import TrashCan from '../../components/svgs/TrashCan.svg';
import { TableSortCell } from '../../components/sorted-table/SortedTable';
import ValidatedInput, { ValidatedState } from '../../components/custom-input/ValidatedInput';
import { Loader, SortedTable } from '../../components';
import { Validators } from '../../types/enums/validators.enum';
import { Designation } from '../../types/enums/designation.enum';
import { StaffInfo, StaffMember } from '../../types/interfaces/staffMember.interfaces';
import { toastMessages } from '../../constants/errorMessages';
import { showToast } from '../../services/toast.service';
import { addStaffMember, deleteStaffMember, getStaffMembers } from '../../api/staffMemberApi';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import './addStaffMember.scss';

type AddStaffMemberProps = {
  staffInfo: StaffInfo;
  setStaffInfo: React.Dispatch<React.SetStateAction<StaffInfo>>;
  onCancel?: () => void;
  onSaved?: () => void;
  validate?: boolean;
  startsValid?: boolean;
  setValid?: (e: boolean) => void;
  setStateWasModified?: (e: boolean) => void;
};

type StaffMemberFormState = {
  firstName: ValidatedState;
  lastName: ValidatedState;
  designation: FilterOption<string>[];
};

const designationOptions: FilterOption<string>[] = [
  { name: 'Veterinarian', value: Designation.Veterinarian },
  { name: 'Embryologist', value: Designation.Embryologist },
  { name: 'Technician', value: Designation.Technician },
];

const AddStaffMember: FC<AddStaffMemberProps> = ({
  staffInfo,
  setStaffInfo,
  onCancel,
  onSaved,
  validate = false,
  startsValid = false,
  setValid = () => {},
  setStateWasModified = () => {},
}): JSX.Element => {
  const [formValues, setFormValues] = useState<StaffMemberFormState>({
    firstName: { value: staffInfo.firstName ?? '', valid: startsValid },
    lastName: { value: staffInfo.lastName ?? '', valid: startsValid },
    designation: [],
  });
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const loadStaffMembers = async () => {
    try {
      setLoading(true);
      const res = await getStaffMembers();
      setStaffList(res.data);
    } catch (err) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      firstName: { value: '', valid: false },
      lastName: { value: '', valid: false },
      designation: [],
    });
    setStaffInfo({ firstName: '', lastName: '', designation: [] });
  };

  useEffect(() => {
    loadStaffMembers();
  }, []);

  const handleDelete = async (id: number) => {
    try {
      await deleteStaffMember(id);
      loadStaffMembers();
      showToast.success(toastMessages.STAFF_MEMBER_DELETED);
      if (onSaved) onSaved();
    } catch (err) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  useEffect(() => {
    setStaffInfo(prev => ({
      ...prev,
      firstName: formValues.firstName.value.trim(),
      lastName: formValues.lastName.value.trim(),
      designation: formValues.designation.map(designation => designation.value),
    }));
  }, [formValues, setStaffInfo]);

  useEffect(() => {
    setValid(formValues.firstName.value.trim().length > 0 && formValues.designation.length > 0);
  }, [formValues, setValid]);

  const isSubmitDisabled = formValues.firstName.value.trim().length === 0 || formValues.designation.length === 0;

  const handleAdd = async () => {
    if (isSubmitDisabled) return;

    const firstName = formValues.firstName.value.trim();
    const lastName = formValues.lastName.value.trim();

    const payload = formValues.designation.map(designation => ({
      firstName,
      lastName,
      jobTitle: designation.value,
    }));

    try {
      await addStaffMember(payload);
      showToast.success(toastMessages.STAFF_MEMBER_ADDED);
      resetForm();
      if (onSaved) onSaved();
      if (onCancel) onCancel();
    } catch (err) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const handleCancel = () => {
    resetForm();
    if (onCancel) onCancel();
  };

  const headers = [
    { displayName: TABLE_HEADER_CONSTANTS.FIRST_NAME, sortable: true },
    { displayName: TABLE_HEADER_CONSTANTS.LAST_NAME },
    { displayName: TABLE_HEADER_CONSTANTS.DESIGNATION },
    { displayName: TABLE_HEADER_CONSTANTS.ACTION },
  ];

  const tableData: TableSortCell[][] = staffList.map(staff => [
    { content: staff.firstName, sortValue: staff.firstName },
    { content: staff.lastName, sortValue: staff.lastName },
    { content: staff.jobTitle, sortValue: staff.jobTitle },
    {
      content: <TrashCan className="trash-can" onClick={() => handleDelete(staff.staffId!)} />,
    },
  ]);

  return (
    <div className="staff-member-modal">
      <div className="modal-container">
        <div className="header">
          <h3>{LABEL_CONSTANTS.ADD_STAFF_MEMBER}</h3>
          <button className="close-btn" onClick={handleCancel} aria-label="Close">
            <CancelSVG />
          </button>
        </div>

        <div className="content">
          <div className="staff-member-form">
            <form>
              <div className="row">
                <div className="input">
                  <ValidatedInput
                    label={`*${LABEL_CONSTANTS.FIRST_NAME}:`}
                    type="text"
                    placeholder="Enter First Name"
                    validatedStateForAutoFill={formValues.firstName}
                    setValidatedState={firstName => {
                      setFormValues({ ...formValues, firstName });
                      setStateWasModified(true);
                    }}
                    validators={[Validators.REQUIRED]}
                    validate={validate}
                  />
                </div>

                <div className="input">
                  <ValidatedInput
                    label={`${LABEL_CONSTANTS.LAST_NAME}:`}
                    type="text"
                    placeholder="Enter Last Name"
                    setValidatedState={lastName => {
                      setFormValues({ ...formValues, lastName });
                      setStateWasModified(true);
                    }}
                    validators={[]}
                    validate={validate}
                  />
                </div>
              </div>

              <div className="row">
                <div className="input">
                  <label>{`*${LABEL_CONSTANTS.DESIGNATION}:`}</label>
                  <MultiSelectDropdown
                    options={designationOptions}
                    value={formValues.designation}
                    onChange={designation => {
                      setFormValues({ ...formValues, designation });
                      setStateWasModified(true);
                    }}
                    validate={validate}
                  />
                </div>

                {formValues.designation.length > 0 && (
                  <div className="input selected-designations">
                    <label>{`${LABEL_CONSTANTS.SELECTED_DESIGNATION}:`}</label>

                    <div className="designation-chip-container">
                      {formValues.designation.map(designation => (
                        <span key={designation.value} className="designation-chip">
                          {designation.name}
                          <button
                            type="button"
                            className="remove-chip"
                            aria-label={`Remove ${designation.name}`}
                            onClick={() => {
                              const newSelection = formValues.designation.filter(
                                designationOption => designationOption.value !== designation.value,
                              );
                              setFormValues({ ...formValues, designation: newSelection });
                            }}>
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </form>

            <div className="button-row">
              <button type="button" className="add-button" onClick={handleAdd} disabled={isSubmitDisabled}>
                {BUTTON_CONSTANTS.SUBMIT}
              </button>
            </div>
            <br />

            <div className="staff-table-container">
              {loading ? <Loader loaderSize="small" /> : <SortedTable headers={headers} data={tableData} height={250} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStaffMember;
